import * as React from 'react';
import { IndFlowAssignmentRepository } from '../../../MedicareProAPI/Repositories/Ind/IndFlowAssignmentRepository';
import { UserFlowStageRepository } from '../../../MedicareProAPI/Repositories/User/UserFlowStageRepository';
import { UserFlowRepository } from '../../../MedicareProAPI/Repositories/User/UserFlowRepository';
import { SIDETAB_POSITION, SideTab, SIDETAB_SIZE } from '../../Components/SideTab';
import { WebAPI } from '../../../MedicareProAPI';
import type { UserScope } from '../../../MedicareProAPI/Entities/User/User';
import type { UserFlow } from '../../../MedicareProAPI/Entities/User/UserFlow';
type StageLite = { id: number; name: string };
type BulkAssignmentResponse = {
  totalInserts: number;
  closedCount: number;
  skippedCount: number;
};
export interface FlowBulkAssignmentProps {
  selectedIndIds: number[];
  userId: number;
  userAccessToken: string;
  operatorName?: string;
  fetching?: boolean;
  show?: boolean;
  onDone?: () => void;
  onCancel?: () => void;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

export const FlowBulkAssignment: React.FC<FlowBulkAssignmentProps> = ({
  selectedIndIds,
  userId,
  userAccessToken,
  operatorName,
  fetching,
  show,
  onDone,
  onCancel,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [batchesCompleted, setBatchesCompleted] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const [allFlows, setAllFlows] = React.useState<UserFlow[]>([]);
  const [flowsLoading, setFlowsLoading] = React.useState(false);
  const [flowsError, setFlowsError] = React.useState<string | null>(null);

  const [userScope, setUserScope] = React.useState<UserScope>();

  const [stagesByFlow, setStagesByFlow] = React.useState<Record<number, StageLite[]>>({});
  const [stagesLoading, setStagesLoading] = React.useState<Record<number, boolean>>({});
  const [stagesError, setStagesError] = React.useState<Record<number, string | null>>({});

  const [selectedFlowId, setSelectedFlowId] = React.useState<number | ''>('');
  const [selectedStageId, setSelectedStageId] = React.useState<number | ''>('');

  const [showSideTab, setShowSideTab] = React.useState<boolean>(show);

  const [skipCount, setSkipCount] = React.useState<number>(0);
  const [insertCount, setInsertCount] = React.useState<number>(0);
  const [closedCount, setClosedCount] = React.useState<number>(0);

  const flowRepository = React.useMemo(() => {
    const r = new UserFlowRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const stageRepository = React.useMemo(() => {
    const r = new UserFlowStageRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const assignRepository = React.useMemo(() => {
    const r = new IndFlowAssignmentRepository();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const webAPI = React.useMemo(() => {
    const r = new WebAPI();
    r.setAccessToken(userAccessToken);
    return r;
  }, [userAccessToken]);

  const fetchUserFlows = async (uId: number, criteria: object = {}) => {
    const res = await flowRepository.getUserFlows(uId, {
      include: ['flowStages', 'user'],
      criteria: criteria,
    });
    return res?.getItems?.() ?? [];
  };

  const uniqFlowsById = (items: any[]) => {
    const seen = new Set<number | string>();
    const out: any[] = [];
    for (const f of items) {
      const id = f.getId();
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      out.push(f);
    }
    return out;
  };

  React.useEffect(() => {
    if (!selectedIndIds || selectedIndIds.length === 0) {
      return;
    }

    let isMounted = true;

    const fetchFlows = async () => {
      setFlowsLoading(true);
      setFlowsError(null);
      try {
        const scope = await webAPI.getUserScope(userId); // { userSubTypeDesignation, ownerId, ... }
        const isCommunity =
          String(scope?.userSubTypeDesignation || '').toLowerCase() === 'community';
        const ownerId = scope?.ownerId;
        setUserScope(scope);

        const promises: Promise<any[]>[] = [fetchUserFlows(userId)];
        if (isCommunity && ownerId && ownerId !== userId) {
          const criteria = { loaAccess: true };
          promises.push(fetchUserFlows(ownerId, criteria));
        }

        const [selfFlows, ownerFlows = []] = await Promise.all(promises);

        const merged = uniqFlowsById([...(selfFlows || []), ...(ownerFlows || [])]);

        if (isMounted) {
          setAllFlows(merged);
        }
      } catch (e: any) {
        if (isMounted) {
          setFlowsError(e?.message || 'Failed to load flows');
        }
      } finally {
        if (isMounted) {
          setFlowsLoading(false);
        }
      }
    };

    fetchFlows();

    return () => {
      isMounted = false;
    };
  }, [selectedIndIds, userId, flowRepository]);

  const loadStagesForFlow = async (flowId: number) => {
    // Dont bother if no one is selected
    if (!selectedIndIds || selectedIndIds.length === 0) return;

    if (stagesByFlow[flowId]?.length) return;

    setStagesLoading((prev) => ({ ...prev, [flowId]: true }));
    setStagesError((prev) => ({ ...prev, [flowId]: null }));

    try {
      const resp = await stageRepository.getUserFlowStages(flowId, { userId }, null);
      const stages: StageLite[] = resp.getItems().map((s: any) => ({
        id: s.id,
        name: s.name,
      }));
      setStagesByFlow((prev) => ({ ...prev, [flowId]: stages }));
    } catch (e: any) {
      setStagesError((prev) => ({
        ...prev,
        [flowId]: e?.message || 'Failed to load stages',
      }));
    } finally {
      setStagesLoading((prev) => ({ ...prev, [flowId]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!selectedFlowId || !selectedStageId) return;
    if (!selectedIndIds || selectedIndIds.length === 0) return;

    setSkipCount(0);
    setInsertCount(0);
    setClosedCount(0);

    setLoading(true);
    setError(null);

    const payloadBase = {
      flowId: selectedFlowId,
      stageId: selectedStageId,
      movedBy: operatorName,
    };

    try {
      const chunks = chunkArray(selectedIndIds, 20);

      for (const chunk of chunks) {
        const results: BulkAssignmentResponse = await assignRepository.createIndFlowAssignmentBulk({
          individualIds: chunk,
          ...payloadBase,
        });

        setSkipCount((prev) => prev + (results.skippedCount ?? 0));
        setInsertCount((prev) => prev + (results.totalInserts ?? 0));
        setClosedCount((prev) => prev + (results.closedCount ?? 0));
      }

      setBatchesCompleted(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to add individuals to flow');
    } finally {
      setLoading(false);
    }
  };

  const selectedStages = stagesByFlow[selectedFlowId] || [];
  const numSelected = selectedIndIds?.length ?? 0;

  return (
    <SideTab
      size={SIDETAB_SIZE.MEDIUM}
      position={SIDETAB_POSITION.RIGHT}
      title="Add to Flow"
      show={showSideTab}
      onClose={() => setShowSideTab(false)}
    >
      <div>
        {fetching && (
          <h5 className="mb-3">
            <i className="far fa-spinner fa-spin"></i> Loading...
          </h5>
        )}
        {!fetching && numSelected > 0 && (
          <h5 className="mb-3">
            Add <b>{numSelected}</b> individual{numSelected === 1 ? '' : 's'} to Flow
          </h5>
        )}

        {numSelected === 0 && !fetching && (
          <div className="alert alert-info p-1" style={{ fontSize: '9pt' }}>
            No individuals selected. Please select at least one individual in the report first.
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Flows */}
        <div className="form-group">
          <label className="small font-weight-bold">Flow</label>
          {flowsLoading && <div className="small text-muted mb-1">Loading flows…</div>}
          {flowsError && <div className="text-danger small mb-1">{flowsError}</div>}

          <select
            className="form-control"
            value={selectedFlowId}
            onChange={async (e) => {
              const v = e.target.value ? Number(e.target.value) : '';
              setSelectedFlowId(v);
              setSelectedStageId('');
              if (typeof v === 'number' && selectedIndIds.length > 0) {
                await loadStagesForFlow(v);
              }
            }}
            disabled={
              flowsLoading || !!flowsError || !selectedIndIds || selectedIndIds.length === 0
            }
          >
            <option value="">{allFlows.length ? 'Select flow…' : 'No flows found'}</option>
            {allFlows.map((f) => (
              <option key={f.getId()} value={f.getId()}>
                {f.getName()} {f.getUser().getUserId() != userId ? <>(Principal)</> : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Stages */}
        <div className="form-group">
          <label className="small font-weight-bold">Stage</label>
          {typeof selectedFlowId === 'number' && stagesLoading[selectedFlowId] && (
            <div className="small text-muted mb-1">Loading stages…</div>
          )}
          {typeof selectedFlowId === 'number' && stagesError[selectedFlowId] && (
            <div className="text-danger small mb-1">{stagesError[selectedFlowId]}</div>
          )}
          <select
            className="form-control"
            value={selectedStageId}
            onChange={(e) => setSelectedStageId(e.target.value ? Number(e.target.value) : '')}
            disabled={
              !selectedFlowId ||
              (typeof selectedFlowId === 'number' && !!stagesLoading[selectedFlowId])
            }
          >
            <option value="">{selectedFlowId ? 'Select stage…' : 'Choose a flow first'}</option>
            {selectedStages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {/* Info */}
        {(loading || batchesCompleted) && (
          <div className="mt-3">
            <div className="badge badge-pill bg-contrast p-2 text-success">
              <b>{insertCount}</b> <span className="font-weight-normal">added to flow/stage</span>
            </div>
            <div className="badge badge-pill bg-contrast p-2 text-info">
              <b>{closedCount}</b> <span className="font-weight-normal">closed current flow</span>
            </div>
            <div className="badge badge-pill bg-contrast p-2 text-warning">
              <b>{skipCount}</b> <span className="font-weight-normal">already in flow/stage</span>
            </div>
          </div>
        )}
        <div className="mt-3 rounded bg-contrast p-3">
          <div>
            <b>How this works</b>
          </div>
          <ul style={{ fontSize: '10pt' }} className="mt-2">
            <li>If an individual is already in this flow/stage they wont be added again</li>
            <li>
              If an individual is in a different stage but same flow, that stage will be closed out
              and they will be added to this new stage
            </li>
            <li>If the individual is in no flow/stage they will be added to this one</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="d-flex align-items-center mt-3">
          <button
            className="btn btn-primary mr-2"
            onClick={handleSubmit}
            disabled={
              loading ||
              numSelected === 0 ||
              batchesCompleted ||
              !selectedFlowId ||
              !selectedStageId ||
              flowsLoading ||
              !!flowsError
            }
          >
            {loading ? 'Adding…' : batchesCompleted ? `Added` : `Add to Flow`}
          </button>

          {onCancel && (
            <button className="btn btn-link" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </SideTab>
  );
};
